import React from 'react';
import { AdminListUser } from '../../types/adminUser';
import { formatDate } from '../../utils/date';

interface UserTableProps {
  users: AdminListUser[];
  onUserClick: (userId: string) => void;
  showProfileColumn?: boolean;
  isIncomplete?: boolean;
  layout?: 'list' | 'two-column';
}

const UserTable: React.FC<UserTableProps> = ({
  users,
  onUserClick,
  showProfileColumn = true,
  isIncomplete = false,
  layout = 'list',
}) => {
  const getProfileStatusText = (user: AdminListUser): string => {
    if (!user.profileExists) return 'No profile';
    if (user.profileCompleted) return 'Completed';
    if (user.lastCompletedStep >= 0) {
      return `Step ${user.currentStep + 1} (completed ${user.lastCompletedStep + 1})`;
    }
    return `Step ${user.currentStep + 1}`;
  };

  const getCurrentStepText = (user: AdminListUser): string => {
    if (!user.profileExists) return 'Not started';
    const currentStep = user.currentStep + 1;
    const lastCompleted = user.lastCompletedStep + 1;
    if (lastCompleted >= 0 && lastCompleted < currentStep) {
      return `Step ${currentStep} (last completed: ${lastCompleted})`;
    }
    return `Step ${currentStep}`;
  };

  const getProfileStatusColor = (user: AdminListUser): string => {
    if (!user.profileExists) return 'text-gray-500';
    if (user.profileCompleted) return 'text-green-400';
    return 'text-yellow-400';
  };

  if (layout === 'two-column') {
    const startups = users.filter(u => u.userType === 'startup');
    const investors = users.filter(u => u.userType === 'investor');

    const renderTwoColumnRow = (user: AdminListUser) => (
      <div
        key={user.id}
        onClick={() => onUserClick(user.id)}
        className="flex items-center gap-3 px-4 py-3 border-b border-gray-700 hover:bg-gray-700 cursor-pointer transition-colors"
      >
        {user.userType === 'startup' && (
          <div className="w-8 h-8 rounded border border-gray-700 bg-gray-800 flex-shrink-0 overflow-hidden">
            {user.thumbnailUrl ? (
              <img
                src={user.thumbnailUrl}
                alt=""
                className="w-full h-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-600 text-[9px]">
                No img
              </div>
            )}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-sm text-white font-medium truncate">
            {user.companyName || user.name || 'N/A'}
          </div>
          <div className="text-xs text-gray-400 truncate">{user.email}</div>
        </div>
        <span
          className={`flex-shrink-0 px-2 text-xs leading-5 font-semibold rounded-full ${
            user.userType === 'startup'
              ? 'bg-yellow-100 text-yellow-800'
              : 'bg-blue-100 text-blue-800'
          }`}
        >
          {user.userType}
        </span>
        {user.userType === 'startup' && (
          <div className="w-[7.5rem] flex-shrink-0 text-right text-xs text-gray-300 whitespace-nowrap">
            {formatDate(user.createdAt, '—')}
          </div>
        )}
      </div>
    );

    return (
      <div className="grid grid-cols-2 gap-6">
        <div className="bg-gray-900 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">
              Startups{' '}
              <span className="ml-1 text-gray-500 font-normal normal-case">
                ({startups.length})
              </span>
            </h3>
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Registered
            </span>
          </div>
          <div className="divide-y divide-gray-700">
            {startups.length === 0 ? (
              <p className="px-4 py-6 text-sm text-gray-500 text-center">No startups</p>
            ) : (
              startups.map(renderTwoColumnRow)
            )}
          </div>
        </div>
        <div className="bg-gray-900 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-700">
            <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">
              Investors{' '}
              <span className="ml-1 text-gray-500 font-normal normal-case">
                ({investors.length})
              </span>
            </h3>
          </div>
          <div className="divide-y divide-gray-700">
            {investors.length === 0 ? (
              <p className="px-4 py-6 text-sm text-gray-500 text-center">No investors</p>
            ) : (
              investors.map(renderTwoColumnRow)
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <div className="min-w-full">
        <div className="bg-gray-800 border-b border-gray-700">
          <div className="flex items-center">
            <div className="flex-shrink-0 w-[250px] px-6 py-3">
              <div className="text-xs font-medium text-gray-300 uppercase tracking-wider text-left">
                Name
              </div>
            </div>
            <div className="flex-shrink-0 w-[120px] px-6 py-3">
              <div className="text-xs font-medium text-gray-300 uppercase tracking-wider">Type</div>
            </div>
            {showProfileColumn && (
              <div className="flex-shrink-0 w-[150px] px-6 py-3">
                <div className="text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Profile
                </div>
              </div>
            )}
            {isIncomplete && (
              <div className="flex-shrink-0 w-[150px] px-6 py-3">
                <div className="text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Current Step
                </div>
              </div>
            )}
            <div className="flex-shrink-0 w-[130px] px-6 py-3">
              <div className="text-xs font-medium text-gray-300 uppercase tracking-wider">
                Approved
              </div>
            </div>
            <div className="flex-shrink-0 w-[130px] px-6 py-3">
              <div className="text-xs font-medium text-gray-300 uppercase tracking-wider">
                Created
              </div>
            </div>
            <div className="flex-shrink-0 w-[130px] px-6 py-3">
              <div className="text-xs font-medium text-gray-300 uppercase tracking-wider">
                Last Login
              </div>
            </div>
          </div>
        </div>

        <div className="divide-y divide-gray-700" style={{ backgroundColor: '#262626' }}>
          {users.map(user => (
            <div
              key={user.id}
              onClick={isIncomplete ? undefined : () => onUserClick(user.id)}
              className={
                isIncomplete
                  ? 'flex items-center border-b border-gray-700 transition-colors'
                  : 'flex items-center border-b border-gray-700 hover:bg-gray-800 cursor-pointer transition-colors'
              }
            >
              <div className="flex-shrink-0 w-[250px] px-6 py-4 text-left flex items-center space-x-3">
                {user.userType === 'startup' && (
                  <div className="w-10 h-10 rounded border border-gray-700 bg-gray-800 flex-shrink-0 overflow-hidden">
                    {user.thumbnailUrl ? (
                      <img
                        src={user.thumbnailUrl}
                        alt=""
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-600 text-[10px]">
                        No img
                      </div>
                    )}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="text-sm text-white font-medium truncate">
                    {user.companyName || user.name || 'N/A'}
                  </div>
                  <div className="text-xs text-gray-400 truncate">{user.email}</div>
                  {user.isPreview && <div className="text-xs text-gray-500 mt-0.5">Preview</div>}
                </div>
              </div>
              <div className="flex-shrink-0 w-[120px] px-6 py-4">
                <span
                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    user.userType === 'startup'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-blue-100 text-blue-800'
                  }`}
                >
                  {user.userType}
                </span>
              </div>
              {showProfileColumn && (
                <div className="flex-shrink-0 w-[150px] px-6 py-4">
                  <div className={`text-sm ${getProfileStatusColor(user)}`}>
                    {getProfileStatusText(user)}
                  </div>
                </div>
              )}
              {isIncomplete && (
                <div className="flex-shrink-0 w-[150px] px-6 py-4">
                  <div className="text-sm text-yellow-400">{getCurrentStepText(user)}</div>
                </div>
              )}
              <div className="flex-shrink-0 w-[130px] px-6 py-4">
                {user.approved ? (
                  <span title={`Approved on ${formatDate(user.approvedAt)}`}>
                    <svg
                      className="w-5 h-5 text-emerald-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </span>
                ) : user.profileExists ? (
                  <span
                    className="w-2.5 h-2.5 bg-amber-500 rounded-full"
                    title="Pending approval"
                  />
                ) : (
                  <span className="text-gray-600">—</span>
                )}
              </div>
              <div className="flex-shrink-0 w-[130px] px-6 py-4 text-sm text-gray-300">
                {formatDate(user.createdAt, 'N/A')}
              </div>
              <div className="flex-shrink-0 w-[130px] px-6 py-4 text-sm text-gray-300">
                {formatDate(user.lastLoginAt, 'N/A')}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UserTable;
